import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import TimeBadge from "@/components/TimeBadge";
import NotificationBell from "@/components/NotificationBell";
import { Sprout, Menu, X, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const navGroups = [
  {
    label: "Dashboard",
    testid: "nav-dashboard",
    to: "/dashboard",
  },
  {
    label: "About",
    testid: "nav-about-group",
    items: [
      { to: "/about", label: "Our Origins", testid: "nav-about", hint: "The village that inspired us" },
      { to: "/foundations", label: "What Bahoi is Made Of", testid: "nav-foundations", hint: "ABCD + time banking + community currency" },
      { to: "/how-it-works", label: "How it Works", testid: "nav-how-it-works", hint: "The 4-step explainer" },
    ],
  },
  {
    label: "Volunteer",
    testid: "nav-volunteer",
    items: [
      { to: "/opportunities", label: "Opportunities", testid: "nav-opportunities", hint: "Browse online & local shifts" },
      { to: "/my-volunteering", label: "My Volunteering", testid: "nav-my-volunteering", hint: "Your signups & status" },
      { to: "/organize", label: "Organize", testid: "nav-organize", hint: "Host an opportunity / verify hours" },
    ],
  },
  {
    label: "Marketplace",
    testid: "nav-market-group",
    items: [
      { to: "/marketplace", label: "Marketplace", testid: "nav-marketplace", hint: "Spend your Time Hours" },
      { to: "/transactions", label: "Transactions", testid: "nav-transactions", hint: "Earnings, escrows & receipts" },
    ],
  },
  {
    label: "Contact",
    testid: "nav-contact",
    to: "/contact",
  },
];

const guestNavGroups = [
  {
    label: "About",
    testid: "guest-nav-about-group",
    items: [
      { to: "/about", label: "Our Origins", testid: "guest-nav-about", hint: "The village that inspired us" },
      { to: "/foundations", label: "What Bahoi is Made Of", testid: "guest-nav-foundations", hint: "ABCD + time banking + community currency" },
      { to: "/how-it-works", label: "How it Works", testid: "guest-nav-how-it-works", hint: "The 4-step explainer" },
    ],
  },
  {
    label: "Opportunities",
    testid: "guest-nav-opportunities",
    to: "/opportunities",
  },
  {
    label: "Marketplace",
    testid: "guest-nav-marketplace",
    to: "/marketplace",
  },
  {
    label: "Contact",
    testid: "guest-nav-contact",
    to: "/contact",
  },
];

function NavDropdown({ group }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const loc = useLocation();
  const activeChild = group.items.some((it) => loc.pathname === it.to || loc.pathname.startsWith(it.to + "/"));

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close when route changes
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={group.testid}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap inline-flex items-center gap-1 transition-colors ${
          activeChild
            ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]"
            : "text-ink-soft hover:text-ink hover:bg-white/40"
        }`}
      >
        {group.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={`${group.testid}-dropdown`}
          role="menu"
          className="absolute left-0 mt-2 min-w-[240px] card-soft shadow-lg z-50 p-1.5 overflow-hidden"
        >
          {group.items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              role="menuitem"
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg transition-colors ${
                  isActive ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]" : "hover:bg-white/60"
                }`
              }
            >
              <div className="font-medium text-sm">{it.label}</div>
              {it.hint && <div className="text-xs opacity-70 mt-0.5">{it.hint}</div>}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const isAuthed = user && typeof user === "object";

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-app">
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-[rgba(244,241,237,0.85)] backdrop-blur-xl border-b border-ink-soft"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex items-center gap-6">
          <Link
            to={isAuthed ? "/dashboard" : "/"}
            data-testid="logo-link"
            className="flex items-center gap-2 group"
          >
            <span className="h-9 w-9 rounded-full bg-[var(--cm-secondary)] text-white grid place-items-center group-hover:rotate-6 transition-transform">
              <Sprout className="h-4 w-4" />
            </span>
            <span className="font-display font-bold text-xl tracking-tight">
              <span className="text-[var(--cm-primary)]">Bahoi</span>
              <span className="text-ink-soft mx-1">·</span>
              cause<span className="font-semibold">match</span>
            </span>
          </Link>

          {isAuthed && (
            <nav className="hidden md:flex items-center gap-1 ml-4">
              {navGroups.map((g) =>
                g.items ? (
                  <NavDropdown key={g.label} group={g} />
                ) : (
                  <NavLink
                    key={g.to}
                    to={g.to}
                    data-testid={g.testid}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                        isActive
                          ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]"
                          : "text-ink-soft hover:text-ink hover:bg-white/40"
                      }`
                    }
                  >
                    {g.label}
                  </NavLink>
                ),
              )}
              {user?.role === "admin" && (
                <NavLink
                  to="/admin"
                  data-testid="nav-admin"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-[var(--cm-primary)] text-white"
                        : "text-[var(--cm-primary)] hover:bg-[var(--cm-primary)]/10"
                    }`
                  }
                >
                  Admin
                </NavLink>
              )}
            </nav>
          )}

          {!isAuthed && (
            <nav className="hidden md:flex items-center gap-1 ml-4" data-testid="guest-nav">
              {guestNavGroups.map((g) =>
                g.items ? (
                  <NavDropdown key={g.label} group={g} />
                ) : (
                  <NavLink
                    key={g.to}
                    to={g.to}
                    data-testid={g.testid}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                        isActive
                          ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]"
                          : "text-ink-soft hover:text-ink hover:bg-white/40"
                      }`
                    }
                  >
                    {g.label}
                  </NavLink>
                ),
              )}
            </nav>
          )}

          <div className="ml-auto flex items-center gap-3">
            {isAuthed ? (
              <>
                <TimeBadge hours={user.time_hours_balance} testId="nav-time-balance" />
                <NotificationBell />
                <Link
                  to="/profile"
                  data-testid="nav-profile-link"
                  className="hidden md:inline-flex text-sm font-medium text-ink-soft hover:text-ink px-3 py-1.5 rounded-full hover:bg-white/40 transition-colors"
                  title="Your profile"
                >
                  @{user.username}
                </Link>
                <button
                  data-testid="logout-btn"
                  className="hidden md:inline-flex btn-outline text-sm px-4 py-1.5 whitespace-nowrap"
                  onClick={handleLogout}
                >
                  Sign out
                </button>
                <button
                  data-testid="mobile-menu-toggle"
                  className="md:hidden p-2 rounded-full border border-ink-soft"
                  onClick={() => setOpen((o) => !o)}
                >
                  {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="header-login-link"
                  className="text-sm font-medium text-ink-soft hover:text-ink"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  data-testid="header-register-link"
                  className="btn-primary text-sm hidden sm:inline-flex"
                >
                  Get started
                </Link>
                <button
                  data-testid="guest-mobile-menu-toggle"
                  className="md:hidden p-2 rounded-full border border-ink-soft"
                  onClick={() => setOpen((o) => !o)}
                  aria-label="Toggle menu"
                >
                  {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </button>
              </>
            )}
          </div>
        </div>

        {isAuthed && open && (
          <div data-testid="mobile-nav" className="md:hidden px-5 pb-4 flex flex-col gap-3">
            {navGroups.map((g) =>
              g.items ? (
                <div key={g.label} className="flex flex-col gap-1">
                  <span className="overline text-ink-soft px-3 pt-2">{g.label}</span>
                  {g.items.map((it) => (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      data-testid={`mobile-${it.testid}`}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `px-3 py-2 rounded-lg text-sm ${
                          isActive ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]" : "hover:bg-white/40"
                        }`
                      }
                    >
                      {it.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={g.to}
                  to={g.to}
                  data-testid={`mobile-${g.testid}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-semibold ${
                      isActive ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]" : "hover:bg-white/40"
                    }`
                  }
                >
                  {g.label}
                </NavLink>
              ),
            )}
            <NavLink
              to="/profile"
              data-testid="mobile-nav-profile"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-semibold ${
                  isActive ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]" : "hover:bg-white/40"
                }`
              }
            >
              Profile · @{user.username}
            </NavLink>
            <button
              data-testid="logout-btn-mobile"
              className="btn-outline mt-2 text-sm"
              onClick={handleLogout}
            >
              Sign out
            </button>
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="mobile-nav-admin"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-semibold ${
                    isActive ? "bg-[var(--cm-primary)] text-white" : "text-[var(--cm-primary)] hover:bg-[var(--cm-primary)]/10"
                  }`
                }
              >
                Admin
              </NavLink>
            )}
          </div>
        )}

        {!isAuthed && open && (
          <div data-testid="guest-mobile-nav" className="md:hidden px-5 pb-4 flex flex-col gap-3">
            {guestNavGroups.map((g) =>
              g.items ? (
                <div key={g.label} className="flex flex-col gap-1">
                  <span className="overline text-ink-soft px-3 pt-2">{g.label}</span>
                  {g.items.map((it) => (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      data-testid={`mobile-${it.testid}`}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `px-3 py-2 rounded-lg text-sm ${
                          isActive ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]" : "hover:bg-white/40"
                        }`
                      }
                    >
                      {it.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={g.to}
                  to={g.to}
                  data-testid={`mobile-${g.testid}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg text-sm font-semibold ${
                      isActive ? "bg-[var(--cm-ink)] text-[var(--cm-surface)]" : "hover:bg-white/40"
                    }`
                  }
                >
                  {g.label}
                </NavLink>
              ),
            )}
            <div className="flex gap-2 mt-3 pt-3 border-t border-ink-soft">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="mobile-login-link"
                className="flex-1 btn-outline text-sm text-center py-2"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                onClick={() => setOpen(false)}
                data-testid="mobile-register-link"
                className="flex-1 btn-primary text-sm text-center py-2"
              >
                Get started
              </Link>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-ink-soft mt-24 py-10 bg-surface">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-full bg-[var(--cm-secondary)] text-white grid place-items-center">
              <Sprout className="h-3.5 w-3.5" />
            </span>
            <span className="font-display font-semibold">Bahoi <span className="text-ink-soft">· causematch</span></span>
          </div>
          <p className="text-xs text-ink-soft max-w-md">
            <span className="font-semibold text-ink">Bahoi</span>{" "}
            <span className="italic">(noun)</span> — a supportive community. Named after a village
            in Indonesia that pooled what little it had into something the whole community could
            share.{" "}
            <Link to="/about" data-testid="footer-about-link" className="underline underline-offset-2 hover:text-ink">
              Read the story
            </Link>
            .
          </p>
          <Link
            to="/contact"
            data-testid="footer-contact-link"
            className="text-xs font-semibold text-[var(--cm-primary)] hover:underline underline-offset-2 whitespace-nowrap"
          >
            Contact us →
          </Link>
        </div>
      </footer>
    </div>
  );
}
